import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { SYNC_PHASES } from "@/data/marketing/homepage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const INTEGRATIONS = [
  "HubSpot",
  "Salesforce",
  "Pipedrive",
  "Google Calendar",
  "Outlook",
  "ServiceTitan",
  "Slack",
  "Notion",
  "Airtable",
];

export default function HomeSync() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section className="w-full bg-white py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Sync · the integrator"
          title={
            <>
              Plugs into the systems you{" "}
              <GradientText>already run</GradientText>
            </>
          }
          subtitle="Every call, chat, QR scan, and email reads from and writes back to the tools you already use. No retyping. No copy-paste. No end-of-day catch-up."
        />

        {/* Integrations strip */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
          <span className="rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] px-4 py-2 text-sm font-semibold text-white shadow-sm">
            700+ integrations
          </span>
          {INTEGRATIONS.map((name) => (
            <span
              key={name}
              className="rounded-full border border-[#E5EAF0] bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
            >
              {name}
            </span>
          ))}
          <span className="rounded-full border border-dashed border-genie-primary/40 px-4 py-2 text-sm font-medium text-genie-primary">
            + bring your own API
          </span>
        </div>

        {/* Before / During / After */}
        <div
          ref={ref}
          className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3"
        >
          {SYNC_PHASES.map((phase, i) => (
            <div
              key={phase.id}
              className={cn(
                "rounded-2xl border border-[#E5EAF0] bg-[#F9FAFC] p-6 md:p-7 transition-all duration-700 ease-out",
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              )}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm">
                <GenieIcon name={phase.genieIcon} size={28} className="h-7 w-7" />
              </div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-genie-primary">
                {phase.label}
              </p>
              <h3 className="mt-1 text-lg font-semibold text-[#222B45]">
                {phase.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                {phase.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Anything custom? Paste an API key, describe the action, and Sync
            designs the recipe.
          </p>
          <Link
            to="/help-genie-for-developers"
            className="mt-3 inline-flex items-center text-sm font-semibold text-genie-primary hover:text-genie-secondary transition-colors"
          >
            Explore the platform
            <ChevronRight className="ml-0.5 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
